import { createSlice } from "@reduxjs/toolkit";

import {
  getAllJobsTrackerListAction,
  getAllJobProgressCommentsAction,
  addJobProgressCommentAction,
  getAllOpenJobsListAction,
  addDocumentLinkAction,
  getDocumentsLinkAction,
  getAllAssignedClientsOpenJobsAction,
  getAllOpenItemsAction,
  getFileAndFolderAction,
  getAdvanceInvoiceCountAction,
  getDailyActiveUserReportAction,
  getUserSessionLoginCountReportAction,
  getMonthlyActiveUserReportAction,
  getUserReportAction,
  getUserWiseInvoiceCountReportAction,
  getAllEInvoiceEWayBillListAction,
  getAllClientLockDateAction,
  getAllExternalUserEmailerAction,
  getMonthlyInvoiceCountGraphAction,
  getAllUserApprovedEntryAction,
  getAllClientBulkUploadReportAction,
  getUserWiseOCRReportAction,
  getClientWiseOCRReportAction,
  getAllBankStatementUploadReportAction,
} from "./actions";
import { initialState } from "./initialState";
import { listingReducer } from "./reducer";

import type { ListingType } from "@/redux/listing/listingTypes";

export const listingSlice = createSlice({
  name: "listing",
  initialState,
  reducers: listingReducer,

  extraReducers: (builder) => {
    builder.addCase(
      getAllJobsTrackerListAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllJobsTrackerListAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          jobTrackerListing: result?.data,
        };
      }
    );

    builder.addCase(
      getAllJobsTrackerListAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      addJobProgressCommentAction.pending,
      (state: ListingType) => ({
        ...state,
        jobProgressCommentsFetching: true,
      })
    );

    builder.addCase(
      addJobProgressCommentAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: success,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      addJobProgressCommentAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        jobProgressCommentsFetching: false,
        jobProgressCommentsSuccess: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getAllJobProgressCommentsAction.pending,
      (state: ListingType) => ({
        ...state,
        jobProgressCommentsFetching: true,
      })
    );

    builder.addCase(
      getAllJobProgressCommentsAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            error: message,
          };
        }

        return {
          ...state,
          jobProgressCommentsFetching: false,
          jobProgressCommentsList: result,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      getAllJobProgressCommentsAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        jobProgressCommentsFetching: false,
        jobProgressCommentsSuccess: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getAllOpenJobsListAction.pending, (state: ListingType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllOpenJobsListAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            jobProgressCommentsSuccess: success,
          };
        }

        return {
          ...state,
          fetching: false,
          openJobsListing: result,
          openJobsListingTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getAllOpenJobsListAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(addDocumentLinkAction.pending, (state: ListingType) => ({
      ...state,
      jobProgressCommentsFetching: true,
    }));

    builder.addCase(
      addDocumentLinkAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { status_code, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: success,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      addDocumentLinkAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        jobProgressCommentsFetching: false,
        jobProgressCommentsSuccess: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getDocumentsLinkAction.pending, (state: ListingType) => ({
      ...state,
      jobProgressCommentsFetching: true,
    }));

    builder.addCase(
      getDocumentsLinkAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { status_code, result, success, message } = payload;

        if (success === 0) {
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          jobProgressCommentsFetching: false,
          statusCode: status_code,
          documentsLinkList: result,
        };
      }
    );

    builder.addCase(
      getDocumentsLinkAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        jobProgressCommentsFetching: false,
        jobProgressCommentsSuccess: 0,
      })
    );

    builder.addCase(
      getAllAssignedClientsOpenJobsAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllAssignedClientsOpenJobsAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            error: message,
            jobProgressCommentsSuccess: success,
          };
        }

        return {
          ...state,
          fetching: false,
          assignedClientsOpenJobsList: result,
        };
      }
    );

    builder.addCase(
      getAllAssignedClientsOpenJobsAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getAllOpenItemsAction.pending, (state: ListingType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getAllOpenItemsAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, data, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          allOpenItemsList: data?.data,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      getAllOpenItemsAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getFileAndFolderAction.pending, (state: ListingType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getFileAndFolderAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, file_folder_res, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          fileFolderCount: file_folder_res,
        };
      }
    );

    builder.addCase(
      getFileAndFolderAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAdvanceInvoiceCountAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAdvanceInvoiceCountAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          invoiceCount: result.data,
        };
      }
    );

    builder.addCase(
      getAdvanceInvoiceCountAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getDailyActiveUserReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getDailyActiveUserReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          dailyActiveUserList: result,
        };
      }
    );

    builder.addCase(
      getDailyActiveUserReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getUserSessionLoginCountReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUserSessionLoginCountReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          userSessionLoginCountList: result,
        };
      }
    );

    builder.addCase(
      getUserSessionLoginCountReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getMonthlyActiveUserReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getMonthlyActiveUserReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          monthlyActiveUserList: result,
        };
      }
    );

    builder.addCase(
      getMonthlyActiveUserReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(getUserReportAction.pending, (state: ListingType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getUserReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          userReportList: result,
        };
      }
    );

    builder.addCase(
      getUserReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getUserWiseInvoiceCountReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUserWiseInvoiceCountReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          userWiseInvoiceCountReportList: result,
        };
      }
    );

    builder.addCase(
      getUserWiseInvoiceCountReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAllEInvoiceEWayBillListAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllEInvoiceEWayBillListAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          allEwayBillEInvoiceList: result,
        };
      }
    );

    builder.addCase(
      getAllClientLockDateAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllClientLockDateAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          allClientLockDateList: result?.data,
          allClientLockDateTotalCount: result?.total_count,
        };
      }
    );

    builder.addCase(
      getAllClientLockDateAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAllExternalUserEmailerAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllExternalUserEmailerAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          allExternalUserEmailerList: result,
          allExternalUserEmailerTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getAllExternalUserEmailerAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getMonthlyInvoiceCountGraphAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getMonthlyInvoiceCountGraphAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          invoiceCountGraphData: result?.data,
        };
      }
    );

    builder.addCase(
      getMonthlyInvoiceCountGraphAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAllUserApprovedEntryAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllUserApprovedEntryAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, data, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          allUserApprovedEntryList: data,
          allUserApprovedEntryTotalCount: total_count,
        };
      }
    );

    builder.addCase(
      getAllUserApprovedEntryAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAllClientBulkUploadReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllClientBulkUploadReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          allClientBulkUploadList: result?.data,
          allClientBulkUploadTotalCount: result?.total_count,
        };
      }
    );

    builder.addCase(
      getAllClientBulkUploadReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getUserWiseOCRReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUserWiseOCRReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          userWiseOCRReportList: result,
        };
      }
    );

    builder.addCase(
      getUserWiseOCRReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getClientWiseOCRReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getClientWiseOCRReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          clientWiseOCRReportList: result?.data,
          clientWiseOCRReportTotalData: result?.total_count,
        };
      }
    );

    builder.addCase(
      getClientWiseOCRReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAllBankStatementUploadReportAction.pending,
      (state: ListingType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllBankStatementUploadReportAction.fulfilled,
      (state: ListingType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
          allBankStatementUploadList: result?.data,
          allBankStatementUploadTotalCount: result?.total_count,
        };
      }
    );

    builder.addCase(
      getAllBankStatementUploadReportAction.rejected,
      (state: ListingType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        fetching: false,
      })
    );
  },
});

export const {
  resetError: resetListingError,
  resetSuccess: resetListingSuccess,
  resetJobProgressCommentsListing,
  resetJobProgressCommentsSuccess,
  resetListingFetching,
  resetJobTrackerListing,
  resetOpenJobsList,
  resetDocumentsLinkList,
  resetAssginedClientsOpenJobsList,
  resetAllOpenItemsLst,
  resetFileFolderCountData,
  resetInvoiveCountData,
  resetAllOpenItemsListTotelCount,
  resetDailyActiveUserList,
  resetUserSessionLoginCountList,
  resetMonthlyActiveUserList,
  resetUserReportList,
  resetUserWiseInvoiceCountReportList,
  resetAllEwayBillEInvoiceList,
  resetAllClientLockDateList,
  resetAllClientLockDateTotalCount,
  resetAllExternalUserEmailerList,
  resetAllExternalUserEmailerTotalCount,
  resetInvoiceMonthlyGraphData,
  resetAlluserApprovedEntryList,
  resetAlluserApprovedEntryTotalCount,
  resetAllClientBulkUploadList,
  resetAllClientBulkUploadTotalCount,
  resetUserWiseOCRReportList,
  resetClientWiseOCRReportList,
  resetAllBankStatementUploadList,
  resetAllBankStatementUploadTotalCount,
} = listingSlice.actions;
